import { useRef } from 'react';
import { Field, Form, Formik } from 'formik';
import {
  Badge,
  Button,
  FormControl,
  FormFile,
  FormGroup,
  FormLabel,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import InputFormGroup from '../util/InputFormGroup';
import { getFileReader } from '../../util/FileReader';
import CheckEdge from './CheckEdge';
import { useHybrid } from '../hooks/useHybrid';
import useTheme from '../hooks/useTheme';
import ProgressModal from '../util/ProgressModal';
import { useHybridOptionsFormHandlers } from './useHybridOptionsFormHandlers';

function HybridOptions({ onComplete, onCancel }) {
  const redStyle = {
    color: 'rgb(230, 0, 0)',
  };
  const [theme] = useTheme();

  const data = useHybrid();
  const formRef = useRef();
  const {
    handleSubmit,
    handleCancel,
    handleEdgeClick,
    errorMsg,
    showEdge,
    setShowEdge,
    edgeDomain,
    showProgress,
  } = useHybridOptionsFormHandlers(onComplete, onCancel, formRef);

  const fileReader = getFileReader();

  if (!data) {
    return null;
  }

  const {
    hasCert,
    certificateKey,
    skypeUser,
    skypeDomain,
    skypeAddress,
    skypePort,
    stratusEmail,
    subjectCN,
  } = data;

  const certLabel = `Trusted Application Identity ${
    hasCert ? `(active CN=${subjectCN})` : ''
  }`;
  const certRequired = !hasCert;
  const certStyle = hasCert ? { color: 'blue' } : {};

  return (
    <>
      <ProgressModal show={showProgress} />
      <Badge variant="danger">{errorMsg}</Badge>

      <br />
      <br />
      <p>
        RealConnect Hybrid provides cloud-based video interop for Skype for
        Business Server 2015 or Lync Server 2013 on-premises users.
      </p>
      <Formik
        initialValues={{
          cloudServiceEmail: stratusEmail || '',
          hasCert: hasCert || false,
          certificateKey: certificateKey || '',
          skypeUser: skypeUser || '',
          skypeDomain: skypeDomain || '',
          skypeAddress: skypeAddress || '',
          skypePort: skypePort || '',
          gmoney: 'not',
        }}
        onSubmit={handleSubmit}
        innerRef={formRef}
      >
        {({ values, isSubmitting, setFieldValue }) => (
          <Form>
            <br />
            <Field name="uploadCert">
              {({ field }) => (
                <FormGroup controlId={field.name}>
                  <FormLabel style={certStyle}>
                    {certLabel}
                    {!hasCert && <span style={redStyle}>*</span>}
                  </FormLabel>
                  <FormFile
                    name={field.name}
                    required={certRequired}
                    title="Select Skype For Business pkcs12 certificate"
                    onChange={(event) => {
                      const file = event.currentTarget.files[0];
                      fileReader.readAsBinaryString(file);
                      setFieldValue('uploadCert', file);
                      field.onChange(event);
                    }}
                  />
                </FormGroup>
              )}
            </Field>

            <InputFormGroup
              type="password"
              name="certificateKey"
              label="Identity Password"
              required
              pattern=".{1,50}"
              title="Between 1 and 50 word characters"
              placeholder="Passphrase used to secure the trusted identity"
            />
            <InputFormGroup
              name="skypeUser"
              label="Skype for Business User"
              required
              pattern=".{1,50}"
              title="Between 1 and 50 word characters"
              placeholder="On-premise Skype user account for conference resolution"
            />
            <Field name="skypeDomain">
              {({ field }) => (
                <FormGroup controlId={field.name}>
                  <FormLabel>
                    Skype for Business Domain <span style={redStyle}>*</span>
                  </FormLabel>
                  <Button
                    name="checkEdgeSubmit"
                    type="button"
                    onClick={handleEdgeClick}
                    variant={theme?.buttonVariant}
                    size="sm"
                    style={{
                      float: 'right',
                      padding: '.50rem .4rem',
                      fontSize: '.875rem',
                      lineHeight: 0.5,
                      borderRadius: '.2rem',
                    }}
                    data-toggle="modal"
                    data-target="#checkEdgeModalDiv"
                    disabled={!values.skypeDomain || isSubmitting}
                  >
                    Check Edge
                  </Button>
                  <FormControl
                    type="text"
                    required
                    {...field}
                    placeholder="Domain of the on-premise Skype deployment"
                    pattern=".{1,50}"
                    title="Between 1 and 50 word characters"
                  />
                </FormGroup>
              )}
            </Field>

            <InputFormGroup
              name="skypeAddress"
              label="Skype for Business Address"
              required
              pattern=".{1,50}"
              title="Between 1 and 50 word characters"
              placeholder="FQDN of front-end server the on-premise Skype user is homed on"
            />
            <InputFormGroup
              name="skypePort"
              label="Skype for Business Port"
              required
              pattern="\d{4,6}"
              title="Between 4 and 6 numbers"
            />
            <InputFormGroup
              type="text"
              label="Poly Cloud Service Email"
              className="form-control"
              name="cloudServiceEmail"
              placeholder="Email address of Poly Cloud Service account"
              pattern="[^@]+@[^@]+\.[a-zA-Z]{2,}"
              title="Valid email address <user>@<domain>"
              required
              disabled
            />
            <Button
              style={{ marginRight: '.15em' }}
              type="submit"
              variant={theme?.buttonVariant}
              size="lg"
              disabled={isSubmitting}
            >
              SUBMIT
            </Button>
            <Button
              variant={theme?.buttonVariant}
              size="lg"
              onClick={handleCancel}
            >
              CANCEL
            </Button>
          </Form>
        )}
      </Formik>
      <CheckEdge
        show={showEdge}
        setShow={setShowEdge}
        skypeDomain={edgeDomain}
      />
    </>
  );
}

HybridOptions.propTypes = {
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
};
export default HybridOptions;
